import React, { useState, useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import "../App.css";
import { getSubscriptionTypesPublic } from "../services/authService";
import playstore from "../images/playstore.png";
import appstore from "../images/appstore.svg";
import screenCoachSeance from "../images/screen-coach-seance.jpg";
import screenCoachDashboard from "../images/screen-coach-dashboard.jpg";
import screenSportifSeances from "../images/screen-sportif-seances.jpg";

function FadeInSection({ children, className = "" }) {
	const ref = useRef(null);
	const [visible, setVisible] = useState(false);

	useEffect(() => {
		const observer = new IntersectionObserver(
			([entry]) => {
				if (entry.isIntersecting) {
					setVisible(true);
					observer.unobserve(entry.target);
				}
			},
			{ threshold: 0.15 }
		);
		if (ref.current) observer.observe(ref.current);
		return () => observer.disconnect();
	}, []);

	return (
		<div
			ref={ref}
			className={`gnv3-fade ${visible ? "visible" : ""} ${className}`}
		>
			{children}
		</div>
	);
}

const FALLBACK_PLANS = {
	starter: ["Entraînements illimités", "Synchronisation cloud", "Templates personnalisés", "Dashboard mobile", "Jusqu'à 10 élèves", "Planification avancée"],
	pro: ["Entraînements illimités", "Synchronisation cloud", "Templates personnalisés", "Dashboard mobile", "Planification avancée", "Dashboard Coach web", "Jusqu'à 100 élèves"],
};

const FAQ_ITEMS = [
	{
		q: "L'app fonctionne-t-elle sans connexion ?",
		a: "Oui, GymNote est 100% offline-first. Toutes tes données sont stockées localement sur ton téléphone. La synchronisation cloud se fait automatiquement dès que tu retrouves une connexion.",
	},
	{
		q: "Puis-je suivre plusieurs élèves ?",
		a: "Oui, avec un abonnement Coach (Starter ou Pro). Le plan Starter te permet de suivre jusqu'à 10 élèves, le plan Pro jusqu'à 100. Chaque élève accepte ton invitation depuis son app.",
	},
	{
		q: "Comment fonctionne la synchronisation ?",
		a: "La synchronisation est automatique : elle se déclenche toutes les 10 secondes quand tu es connecté. En cas de conflit, GymNote conserve toujours la version la plus récente.",
	},
	{
		q: "Est-ce que mes données sont sauvegardées ?",
		a: "Absolument. Tes entraînements, mesures et exercices sont sauvegardés en temps réel sur nos serveurs sécurisés. Tu peux aussi exporter tes données à tout moment depuis les paramètres.",
	},
	{
		q: "Puis-je annuler mon abonnement à tout moment ?",
		a: "Oui, sans engagement. Tu peux annuler ton abonnement depuis ton dashboard directement dans l'app. La résiliation prend effet à la fin de la période en cours.",
	},
	{
		q: "L'app est-elle disponible sur iOS et Android ?",
		a: "GymNote est disponible sur les deux plateformes : télécharge-la sur l'App Store (iPhone/iPad) ou sur Google Play (Android). L'expérience est identique sur les deux.",
	},
	{
		q: "Comment inviter un élève ?",
		a: "Depuis l'app, active le mode Coach puis rends-toi dans la section GymPartners. Génère un code d'invitation unique que tu envoies à ton élève. Une fois accepté, tu as accès à ses séances.",
	},
	{
		q: "Y a-t-il une période d'essai ?",
		a: "Oui, tu bénéficies de 7 jours gratuits pour tester le mode Coach sans aucune restriction. Aucune carte bancaire requise pour démarrer l'essai.",
	},
];

const TESTIMONIALS = [
	{
		quote: "Depuis que j'utilise GymNote, je programme les séances de mes élèves en 10 minutes au lieu d'une heure. Le dashboard web est vraiment pratique pour ajuster en temps réel.",
		name: "Thomas R.",
		role: "Coach sportif indépendant",
		initials: "TR",
	},
	{
		quote: "L'app est super fluide et le mode hors-ligne est parfait pour la salle. Je vois mes perfs précédentes directement pendant ma séance, ça motive vraiment à se dépasser.",
		name: "Marie L.",
		role: "Athlète amateur",
		initials: "ML",
	},
	{
		quote: "J'ai 23 élèves et GymNote Pro me permet de tout gérer depuis mon téléphone ou mon ordinateur. La synchronisation instantanée change vraiment la façon dont je travaille.",
		name: "Julien M.",
		role: "Coach CrossFit certifié",
		initials: "JM",
	},
];

const FEATURES = [
	{
		icon: "mdi-clipboard-text-clock-outline",
		title: "Saisie éclair",
		text: "Reprends tes charges précédentes en un tap. Séries, reps, RPE et tempo en quelques secondes.",
	},
	{
		icon: "mdi-chart-line",
		title: "Courbes de force",
		text: "Visualise l'évolution de chaque exercice, ton volume hebdo et tes 1RM estimés.",
	},
	{
		icon: "mdi-dumbbell",
		title: "Programmes",
		text: "Crée tes splits ou suis un programme. Push/Pull/Legs, full-body, force… tout est modulable.",
	},
	{
		icon: "mdi-trophy-outline",
		title: "Records & badges",
		text: "Chaque PR est détecté automatiquement et fêté. Garde ta motivation au sommet.",
		variant: "orange",
	},
	{
		icon: "mdi-timer-outline",
		title: "Minuteur de repos",
		text: "Chrono intelligent entre les séries, avec rappels et durée adaptée à l'exercice.",
	},
	{
		icon: "mdi-account-supervisor-outline",
		title: "Lien coach",
		text: "Partage tes séances avec ton coach et reçois ses ajustements en temps réel.",
	},
];

const TRUST_ITEMS = [
	{ icon: "mdi-cloud-sync", label: "Sync multi-appareils" },
	{ icon: "mdi-wifi-off", label: "Mode hors-ligne" },
	{ icon: "mdi-cancel", label: "Sans pub" },
	{ icon: "mdi-chart-line", label: "Stats détaillées" },
	{ icon: "mdi-trending-up", label: "Progression assistée" },
];

function FaqAccordion() {
	const [openIndex, setOpenIndex] = useState(null);
	const toggle = (i) => setOpenIndex(openIndex === i ? null : i);

	return (
		<FadeInSection>
			<section className="gnv3-sec gnv3-faq" id="faq">
				<div className="gnv3-wrap">
					<div className="gnv3-sec-head center">
						<span className="gnv3-eyebrow">FAQ</span>
						<h2 className="gnv3-h2">Tes questions, nos réponses.</h2>
						<p className="gnv3-sub">Tout ce que tu veux savoir avant de te lancer.</p>
					</div>
					<div className="gnv3-faq-list">
						{FAQ_ITEMS.map((item, i) => (
							<div
								key={i}
								className={`gnv3-faq-item${openIndex === i ? " open" : ""}`}
							>
								<button
									className="gnv3-faq-q"
									onClick={() => toggle(i)}
									aria-expanded={openIndex === i}
								>
									<span>{item.q}</span>
									<span className="gnv3-faq-icon" aria-hidden="true">+</span>
								</button>
								<div className="gnv3-faq-a">
									<p>{item.a}</p>
								</div>
							</div>
						))}
					</div>
				</div>
			</section>
		</FadeInSection>
	);
}

function Home() {
	const [billingPeriod, setBillingPeriod] = useState("monthly");
	const [plans, setPlans] = useState(null);

	useEffect(() => {
		const fetchPlans = async () => {
			const result = await getSubscriptionTypesPublic();
			if (result.success) {
				setPlans(result.types);
			}
		};
		fetchPlans();
	}, []);

	const starterPlans = plans?.filter(p => p.libelle?.toLowerCase().includes("starter") && p.prix > 0) || [];
	const proPlans = plans?.filter(p => p.libelle?.toLowerCase().includes("pro") && p.prix > 0) || [];
	const starterMonthly = starterPlans.find(p => p.dureeMois === 1);
	const starterYearly = starterPlans.find(p => p.dureeMois === 12);
	const proMonthly = proPlans.find(p => p.dureeMois === 1);
	const proYearly = proPlans.find(p => p.dureeMois === 12);

	const starterFeatures = (starterMonthly || starterYearly)?.fonctionnalites?.map(f => f.libelle) || FALLBACK_PLANS.starter;
	const proFeatures = (proMonthly || proYearly)?.fonctionnalites?.map(f => f.libelle) || FALLBACK_PLANS.pro;

	const starterPlan = billingPeriod === "yearly" && starterYearly ? starterYearly : starterMonthly;
	const starterPrice = starterPlan?.prix ?? (billingPeriod === "monthly" ? 9.99 : 99.99);
	const proPlan = billingPeriod === "yearly" && proYearly ? proYearly : proMonthly;
	const proPrice = proPlan?.prix ?? (billingPeriod === "monthly" ? 19.99 : 199.99);
	const starterIsYearly = starterPlan?.dureeMois === 12;
	const proIsYearly = proPlan?.dureeMois === 12;

	return (
		<div className="gnv3-page">
			{/* ============ HERO ============ */}
			<section className="gnv3-hero">
				<div className="gnv3-wrap gnv3-hero-grid">
					<div className="gnv3-hero-copy">
						<span className="gnv3-eyebrow">Suivi de performance & coaching</span>
						<h1 className="gnv3-h1">
							Chaque rep compte.<br />
							<span className="gnv3-accent">Suis ta progression.</span>
						</h1>
						<p className="gnv3-lead">
							GymNote enregistre tes séances, visualise tes courbes de force et te connecte
							à ton coach, sans friction.
						</p>
						<div className="gnv3-hero-actions">
							<a
								className="gnv3-btn gnv3-btn-primary"
								href="https://apps.apple.com/fr/app/gymnote-suivi-muscu-coach/id6470710692"
								target="_blank"
								rel="noopener noreferrer"
							>
								<span className="mdi mdi-apple"></span>App Store
							</a>
							<a
								className="gnv3-btn gnv3-btn-ghost"
								href="https://play.google.com/store/apps/details?id=com.frenchmatrix.GymNote"
								target="_blank"
								rel="noopener noreferrer"
							>
								<span className="mdi mdi-google-play"></span>Google Play
							</a>
						</div>
						<div className="gnv3-store-note">
							<span className="mdi mdi-star"></span>
							5/5 · plus de 20 000 performances enregistrées sur GymNote
						</div>
						<div className="gnv3-hero-stats">
							<div>
								<div className="gnv3-stat-num">+38%</div>
								<div className="gnv3-stat-lbl">de force en moyenne / 6 mois</div>
							</div>
							<div>
								<div className="gnv3-stat-num">2 min</div>
								<div className="gnv3-stat-lbl">pour logger une séance</div>
							</div>
							<div>
								<div className="gnv3-stat-num">100%</div>
								<div className="gnv3-stat-lbl">hors-ligne, sans pub</div>
							</div>
						</div>
					</div>

					<div className="gnv3-hero-visual">
						<div className="gnv3-glow gnv3-glow-teal"></div>
						<div className="gnv3-glow gnv3-glow-deep"></div>
						<div className="gnv3-deco-bar" style={{ width: "170px", top: "30px", left: 0, transform: "rotate(-8deg)" }}></div>
						<div className="gnv3-deco-bar" style={{ width: "120px", bottom: "50px", right: "10px", transform: "rotate(6deg)" }}></div>

						<div className="gnv3-phone">
							<div className="gnv3-phone-screen">
								<img src={screenSportifSeances} alt="App GymNote (séance)" />
							</div>
						</div>
					</div>
				</div>
			</section>

			{/* ============ TRUST ============ */}
			<section className="gnv3-trust">
				<div className="gnv3-wrap gnv3-trust-inner">
					{TRUST_ITEMS.map((it, i) => (
						<div className="gnv3-trust-it" key={i}>
							<span className={`mdi ${it.icon}`}></span>
							{it.label}
						</div>
					))}
				</div>
			</section>

			{/* ============ FEATURES ============ */}
			<FadeInSection>
				<section className="gnv3-sec" id="features">
					<div className="gnv3-wrap">
						<div className="gnv3-sec-head">
							<span className="gnv3-eyebrow">Conçu pour progresser</span>
							<h2 className="gnv3-h2">Tout ton entraînement, structuré.</h2>
							<p className="gnv3-sub">
								Logge vite, analyse en profondeur. GymNote transforme chaque séance
								en données exploitables.
							</p>
						</div>
						<div className="gnv3-feat-grid">
							{FEATURES.map((f, i) => (
								<article key={i} className="gnv3-feat-card">
									<div className={`gnv3-feat-ic${f.variant === "orange" ? " gnv3-feat-ic-orange" : ""}`}>
										<span className={`mdi ${f.icon}`}></span>
									</div>
									<h3 className="gnv3-feat-title">{f.title}</h3>
									<p className="gnv3-feat-text">{f.text}</p>
								</article>
							))}
						</div>
					</div>
				</section>
			</FadeInSection>

			{/* ============ PROGRESS (dark band) ============ */}
			<FadeInSection>
				<section className="gnv3-sec gnv3-sec-band" id="progress">
					<div className="gnv3-band-dark">
						<div className="gnv3-band-glow"></div>
						<div className="gnv3-prog-grid">
							<div>
								<span className="gnv3-eyebrow gnv3-eyebrow-on-dark">Analyse de progression</span>
								<h2 className="gnv3-h2 gnv3-h2-on-dark">
									Les chiffres qui prouvent que tu progresses.
								</h2>
								<p className="gnv3-sub gnv3-sub-on-dark">
									GymNote relie chaque répétition à une tendance. Tu vois ce qui marche,
									et tu ajustes avant de stagner.
								</p>
								<ul className="gnv3-band-list">
									<li>
										<span className="mdi mdi-trending-up"></span>
										<span>Évolution du 1RM estimé, exercice par exercice</span>
									</li>
									<li>
										<span className="mdi mdi-chart-bar"></span>
										<span>Volume, tonnage et fréquence par groupe musculaire</span>
									</li>
									<li>
										<span className="mdi mdi-bullseye-arrow"></span>
										<span>Objectifs personnalisés avec suivi visuel</span>
									</li>
								</ul>
							</div>
							<div className="gnv3-chart-card">
								<div className="gnv3-chart-head">
									<div>
										<div className="gnv3-chart-t">Développé couché · 1RM estimé</div>
										<div className="gnv3-chart-big">112,5 kg</div>
									</div>
									<div className="gnv3-chart-delta">
										<span className="mdi mdi-arrow-top-right"></span> +18%
									</div>
								</div>
								<svg className="gnv3-chart-svg" viewBox="0 0 480 180" preserveAspectRatio="none">
									<defs>
										<linearGradient id="gnv3-fill" x1="0" y1="0" x2="0" y2="1">
											<stop offset="0%" stopColor="#30a49a" stopOpacity=".45" />
											<stop offset="100%" stopColor="#30a49a" stopOpacity="0" />
										</linearGradient>
									</defs>
									<line x1="0" y1="45" x2="480" y2="45" stroke="rgba(234,241,237,.10)" strokeWidth="1" />
									<line x1="0" y1="95" x2="480" y2="95" stroke="rgba(234,241,237,.10)" strokeWidth="1" />
									<line x1="0" y1="145" x2="480" y2="145" stroke="rgba(234,241,237,.10)" strokeWidth="1" />
									<path d="M0,150 L80,138 L160,120 L240,110 L320,78 L400,64 L480,40 L480,180 L0,180 Z" fill="url(#gnv3-fill)" />
									<path d="M0,150 L80,138 L160,120 L240,110 L320,78 L400,64 L480,40" fill="none" stroke="#30a49a" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" />
									<circle cx="480" cy="40" r="5" fill="#30a49a" stroke="#17211E" strokeWidth="2.5" />
									<circle cx="320" cy="78" r="4" fill="#17211E" stroke="#30a49a" strokeWidth="2.5" />
								</svg>
								<div className="gnv3-chart-x">
									<span>Jan</span>
									<span>Fév</span>
									<span>Mar</span>
									<span>Avr</span>
									<span>Mai</span>
									<span>Juin</span>
								</div>
							</div>
						</div>
					</div>
				</section>
			</FadeInSection>

			{/* ============ COACH MODE (orange) ============ */}
			<FadeInSection>
				<section className="gnv3-sec gnv3-coach" id="coach">
					<div className="gnv3-wrap gnv3-coach-grid">
						<div className="gnv3-coach-panel">
							<div className="gnv3-coach-panel-head">
								<div className="gnv3-coach-ic">
									<span className="mdi mdi-whistle"></span>
								</div>
								<div>
									<div className="gnv3-coach-panel-ttl">Mes athlètes</div>
									<div className="gnv3-coach-panel-sub">vue coach · 8 actifs</div>
								</div>
							</div>
							<div className="gnv3-ath">
								<div className="gnv3-ath-av">AL</div>
								<div className="gnv3-ath-info">
									<div className="gnv3-ath-nm">Alex L.</div>
									<div className="gnv3-ath-meta">Push/Pull/Legs · J3</div>
								</div>
								<span className="gnv3-badge up">+6% vol.</span>
							</div>
							<div className="gnv3-ath">
								<div className="gnv3-ath-av">MM</div>
								<div className="gnv3-ath-info">
									<div className="gnv3-ath-nm">Maya M.</div>
									<div className="gnv3-ath-meta">Force 5×5 · J2</div>
								</div>
								<span className="gnv3-badge up">PR squat</span>
							</div>
							<div className="gnv3-ath">
								<div className="gnv3-ath-av">TD</div>
								<div className="gnv3-ath-info">
									<div className="gnv3-ath-nm">Tom D.</div>
									<div className="gnv3-ath-meta">Full-body · repos</div>
								</div>
								<span className="gnv3-badge flat">stable</span>
							</div>
							<div className="gnv3-ath">
								<div className="gnv3-ath-av">SK</div>
								<div className="gnv3-ath-info">
									<div className="gnv3-ath-nm">Sara K.</div>
									<div className="gnv3-ath-meta">Hypertrophie · J4</div>
								</div>
								<div className="gnv3-mini-bars">
									<i style={{ height: "40%" }}></i>
									<i style={{ height: "60%" }}></i>
									<i style={{ height: "55%" }}></i>
									<i style={{ height: "80%" }}></i>
									<i style={{ height: "100%" }}></i>
								</div>
							</div>
						</div>
						<div>
							<span className="gnv3-pill-tag">
								<span className="mdi mdi-account-tie"></span>Espace coach
							</span>
							<span className="gnv3-eyebrow gnv3-eyebrow-orange">Un mode dédié</span>
							<h2 className="gnv3-h2">
								Pilote tous tes athlètes depuis un seul tableau de bord.
							</h2>
							<p className="gnv3-sub">
								Le mode coach passe l'interface en orange et débloque les outils
								de suivi à distance, sur ton téléphone et sur le web.
							</p>
							<ul className="gnv3-coach-points">
								<li>
									<span className="mdi mdi-eye-outline"></span>
									<span>Vois les séances de chaque athlète en temps réel</span>
								</li>
								<li>
									<span className="mdi mdi-pencil-ruler"></span>
									<span>Assigne et ajuste les programmes à distance</span>
								</li>
								<li>
									<span className="mdi mdi-message-text-outline"></span>
									<span>Commente une série, corrige une charge, encourage</span>
								</li>
								<li>
									<span className="mdi mdi-alert-circle-outline"></span>
									<span>Repère les stagnations avant qu'elles s'installent</span>
								</li>
							</ul>
							<Link to="/coach" className="gnv3-btn gnv3-btn-orange">
								<span className="mdi mdi-arrow-right"></span>Découvrir le mode coach
							</Link>
						</div>
					</div>
				</section>
			</FadeInSection>

			{/* ============ APP SHOWCASE (screens) ============ */}
			<FadeInSection>
				<section className="gnv3-sec gnv3-showcase">
					<div className="gnv3-wrap">
						<div className="gnv3-sec-head center">
							<span className="gnv3-eyebrow">L'app en image</span>
							<h2 className="gnv3-h2">Pensée pour la salle. Affûtée pour les data.</h2>
							<p className="gnv3-sub">
								Une interface qui se fait oublier pendant la séance, et qui te
								rappelle exactement où tu en es quand tu en sors.
							</p>
						</div>
						<div className="gnv3-showcase-grid">
							<div className="gnv3-showcase-card">
								<div className="gnv3-showcase-phone">
									<img src={screenSportifSeances} alt="Liste des séances sportif" />
								</div>
								<h3>Tes perfs, en main</h3>
								<p>Tes anciennes perfs s'affichent direct. À toi de les dépasser.</p>
							</div>
							<div className="gnv3-showcase-card">
								<div className="gnv3-showcase-phone">
									<img src={screenCoachDashboard} alt="Dashboard mobile coach" />
								</div>
								<h3>Mode coach mobile</h3>
								<p>Indicateurs de régularité, progression, régression : qui décroche, qui cartonne.</p>
							</div>
							<div className="gnv3-showcase-card">
								<div className="gnv3-showcase-phone">
									<img src={screenCoachSeance} alt="Édition d'une séance en mode coach" />
								</div>
								<h3>Ajuste en temps réel</h3>
								<p>Édite chaque séance pendant qu'elle se passe. Tes élèves voient les changements direct.</p>
							</div>
						</div>
					</div>
				</section>
			</FadeInSection>

			{/* ============ TESTIMONIALS ============ */}
			<FadeInSection>
				<section className="gnv3-sec">
					<div className="gnv3-wrap">
						<div className="gnv3-sec-head center">
							<span className="gnv3-eyebrow">Ils ne s'entraînent plus à l'aveugle</span>
							<h2 className="gnv3-h2">Approuvé par les passionnés de fer.</h2>
						</div>
						<div className="gnv3-quote-grid">
							{TESTIMONIALS.map((t, i) => (
								<div key={i} className="gnv3-quote">
									<div className="gnv3-stars">★★★★★</div>
									<p>« {t.quote} »</p>
									<div className="gnv3-quote-who">
										<div className="gnv3-quote-av">{t.initials}</div>
										<div>
											<div className="gnv3-quote-nm">{t.name}</div>
											<div className="gnv3-quote-role">{t.role}</div>
										</div>
									</div>
								</div>
							))}
						</div>
					</div>
				</section>
			</FadeInSection>

			{/* ============ PRICING ============ */}
			<FadeInSection>
				<section className="gnv3-sec" id="pricing">
					<div className="gnv3-wrap">
						<div className="gnv3-sec-head center">
							<span className="gnv3-eyebrow">Tarifs</span>
							<h2 className="gnv3-h2">Commence gratuitement.</h2>
							<p className="gnv3-sub">Passe au niveau supérieur quand tu es prêt. Sans engagement.</p>
						</div>
						<div className="gnv3-billing-toggle">
							<span className={billingPeriod === "monthly" ? "active" : ""}>
								Mensuel
							</span>
							<label className="gnv3-switch">
								<input
									type="checkbox"
									checked={billingPeriod === "yearly"}
									onChange={() =>
										setBillingPeriod(billingPeriod === "monthly" ? "yearly" : "monthly")
									}
								/>
								<span className="gnv3-slider"></span>
							</label>
							<span className={billingPeriod === "yearly" ? "active" : ""}>
								Annuel <span className="gnv3-savings">-17%</span>
							</span>
						</div>

						<div className="gnv3-price-grid">
							<div className="gnv3-price">
								<div className="gnv3-pn">Sportif</div>
								<div className="gnv3-pp">0€<span> / toujours</span></div>
								<div className="gnv3-pd">Pour démarrer et logger tes séances.</div>
								<ul>
									<li><span className="mdi mdi-check-circle-outline"></span>Séances illimitées</li>
									<li><span className="mdi mdi-check-circle-outline"></span>Bibliothèque d'exercices</li>
									<li><span className="mdi mdi-check-circle-outline"></span>Minuteur de repos</li>
									<li><span className="mdi mdi-check-circle-outline"></span>Suivi du poids de corps</li>
									<li><span className="mdi mdi-check-circle-outline"></span>Partage avec tes Gymbros</li>
								</ul>
							</div>
							<div className="gnv3-price gnv3-price-feature">
								<span className="gnv3-flag">Le plus populaire</span>
								<div className="gnv3-pn">Coach Starter</div>
								<div className="gnv3-pp">
									{starterPrice}€<span>{starterIsYearly ? " / an" : " / mois"}</span>
								</div>
								{starterIsYearly && (
									<div className="gnv3-pd">soit {(starterPrice / 12).toFixed(2)}€/mois</div>
								)}
								{!starterIsYearly && (
									<div className="gnv3-pd">Pour celles et ceux qui débutent le coaching.</div>
								)}
								<ul>
									{starterFeatures.map((f, i) => (
										<li key={i}><span className="mdi mdi-check-circle-outline"></span>{f}</li>
									))}
								</ul>
							</div>
							<div className="gnv3-price">
								<div className="gnv3-pn">Coach Pro</div>
								<div className="gnv3-pp">
									{proPrice}€<span>{proIsYearly ? " / an" : " / mois"}</span>
								</div>
								{proIsYearly && (
									<div className="gnv3-pd">soit {(proPrice / 12).toFixed(2)}€/mois</div>
								)}
								{!proIsYearly && (
									<div className="gnv3-pd">Pour les coachs qui suivent leurs athlètes à l'échelle.</div>
								)}
								<ul>
									{proFeatures.map((f, i) => (
										<li key={i}><span className="mdi mdi-check-circle-outline"></span>{f}</li>
									))}
								</ul>
							</div>
						</div>
						<p className="gnv3-price-note">
							<span className="mdi mdi-apple"></span>
							<span className="mdi mdi-google-play"></span>
							Les abonnements se souscrivent directement dans l'app, sur l'App Store ou Google Play.
						</p>
					</div>
				</section>
			</FadeInSection>

			{/* ============ FAQ ============ */}
			<FaqAccordion />

			{/* ============ FINAL CTA ============ */}
			<FadeInSection>
				<section className="gnv3-sec gnv3-sec-band" id="download">
					<div className="gnv3-cta-band">
						<div className="gnv3-band-glow"></div>
						<h2 className="gnv3-h2 gnv3-h2-on-dark">Ta prochaine séance mérite GymNote.</h2>
						<p className="gnv3-sub gnv3-sub-on-dark">
							Télécharge l'app, logge ta première séance et regarde ta progression
							prendre forme.
						</p>
						<div className="gnv3-cta-actions">
							<a
								href="https://apps.apple.com/fr/app/gymnote-suivi-muscu-coach/id6470710692"
								target="_blank"
								rel="noopener noreferrer"
								className="gnv3-store-link"
							>
								<img src={appstore} alt="Télécharger dans l'App Store" />
							</a>
							<a
								href="https://play.google.com/store/apps/details?id=com.frenchmatrix.GymNote"
								target="_blank"
								rel="noopener noreferrer"
								className="gnv3-store-link"
							>
								<img src={playstore} alt="Disponible sur Google Play" />
							</a>
						</div>
					</div>
				</section>
			</FadeInSection>
		</div>
	);
}

export default Home;
